import type { UserScreenNode } from '@/shared/types/system';

/** DAL'ın döndürdüğü ham satır (SystemScreenUser + iç içe Screen). */
export interface SystemScreenUserRaw {
  screenUserId: string;
  screenId: string;
  userId: string;
  visible: boolean;
  favorite: boolean;
  screen: {
    screenId: string;
    parentScreenId: string | null;
    displayOrder: number;
    screenCaption: string;
    objectId: string | null;
    iconName: string | null;
    iconId: string | null;
    screenTypeId: string | null;
    active: boolean;
    filterMenu: boolean;
    screenType?: { screenTypeName?: string } | null;
    icon?: { icon?: string } | null;
    childScreens?: unknown[] | null;
  } | null;
}

// Screen types that should not appear in the mobile menu
// Web: tüm ekran tipleri menüde (yetki UserScreens ile). Mobil bu tipleri gizliyordu.
const EXCLUDED_SCREEN_TYPES: string[] = [];

/**
 * Düz SystemScreenUser[] listesini menü ağacına çevirir.
 *
 * Yetki ekran bazında: bir modüle yetkisi olmayan kullanıcının altındaki ekrana yetkisi
 * olabilir. Sunucu (MenuScreens ucu) bu durumda gezinmek için gereken üst modülleri de
 * `visible=false` ile gönderiyor — burada `accessible: false` oluyorlar: klasör olarak
 * çizilir, tıklanamaz, favorilenemez. Web'in davranışı budur; daha önce yalnızca yetkili
 * satırlar alındığı için böyle bir ekran kök seviyesine düşüyor ve hiyerarşi kayboluyordu.
 */
export function buildMenuTree(raw: SystemScreenUserRaw[]): UserScreenNode[] {
  const items = raw.filter((r) => r.screen && r.screen.active);

  // Create flat node map
  const nodeMap = new Map<string, UserScreenNode>();
  for (const item of items) {
    const s = item.screen!;
    const typeName = s.screenType?.screenTypeName || null;

    // Exclude internal screen types
    if (typeName && EXCLUDED_SCREEN_TYPES.includes(typeName.toLowerCase())) {
      continue;
    }

    nodeMap.set(s.screenId, {
      screenId: s.screenId,
      screenUserId: item.screenUserId,
      screenName: s.screenCaption,
      caption: s.screenCaption,
      iconName: s.iconName,
      iconId: s.iconId ?? null,
      parentScreenId: s.parentScreenId,
      objectId: s.objectId,
      screenTypeName: typeName,
      displayOrder: s.displayOrder,
      // Yalnızca gezinme için gelen üst modül favorilenemez: kullanıcının o ekrana
      // yetkisi yok, favori listesinde açılamayan bir satır olurdu.
      favorite: item.visible === false ? false : item.favorite,
      accessible: item.visible !== false,
      children: [],
    });
  }

  // Build tree - only attach to parent if parent exists in the map
  const roots: UserScreenNode[] = [];
  for (const node of nodeMap.values()) {
    if (node.parentScreenId && nodeMap.has(node.parentScreenId)) {
      nodeMap.get(node.parentScreenId)!.children.push(node);
    } else if (!node.parentScreenId) {
      // Only add true root items (no parent)
      roots.push(node);
    } else {
      // Ata zinciri sunucuda tamamlanıyor; buraya düşmek ancak ata pasif (Active=false)
      // olduğunda mümkün. O durumda ekranı gizlemek yerine kökte göstermek daha iyi:
      // kullanıcının yetkisi var ve başka türlü hiç erişemez.
      roots.push(node);
    }
  }

  /**
   * Boş dalları eler. Kalma ölçütü web'deki `hasChildren || isScreen` ile aynı: çocuğu
   * olan klasör kalır, ekran olan satır kalır. "Ekran" derken yalnızca ObjectId'ye bakmak
   * yetmiyor — yerleşik (BuiltIn) ekranların ObjectId'si yok, ekran TİPİ var; eskiden
   * bunlar menüden düşüyordu.
   *
   * Yetkisiz üst modül çocuksuz kalırsa da elenir: varlık sebebi altındaki ekranlar.
   * ObjectId'si olsa bile tıklanabilir bir satıra dönüşmemeli, kullanıcının o ekrana
   * yetkisi yok.
   */
  function pruneEmpty(nodes: UserScreenNode[]): UserScreenNode[] {
    return nodes.filter((node) => {
      node.children = pruneEmpty(node.children);
      if (node.children.length > 0) return true;
      if (!node.accessible) return false;
      const isMainMenu = (node.screenTypeName ?? '').toLowerCase() === 'mainmenu';
      return node.objectId != null || (!isMainMenu && node.screenTypeName != null);
    });
  }

  // Sort by displayOrder
  function sortNodes(nodes: UserScreenNode[]): UserScreenNode[] {
    nodes.sort((a, b) => a.displayOrder - b.displayOrder);
    for (const n of nodes) {
      if (n.children.length > 0) sortNodes(n.children);
    }
    return nodes;
  }

  return sortNodes(pruneEmpty(roots));
}
